import React, { useState } from 'react';
import { Star, Phone, Mail, MapPin, Clock, DollarSign, User, Award, ChevronDown, ChevronUp, Camera, Calendar, Zap, Info } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { serviceDetails } from '../../../Redux/Slices/Pages/services/servicesSlice';
import { GetProfilewithWorks } from '../../../Redux/Slices/Pages/UserProfile/ProfileSlice';

const WorkerProfileCard = ({workerData}) => {
  const navigate =useNavigate()
  const dispatch =useDispatch();


  const [isExpanded, setIsExpanded] = useState(false);  const defaultAvailability = {
    today: { status: 'busy' },
    tomorrow: { status: 'busy' },
    thisWeek: { status: 'busy' },
    nextWeek: { status: 'busy' }
  };

  const availabilityData = workerData?.availability || defaultAvailability;

  const [selectedAvailability, setSelectedAvailability] = useState('today');

  const viewDeailsHandler = (workerId)=>{
    dispatch(GetProfilewithWorks(workerId));
    navigate(`/serviceDetails/${workerId}`)
  }

  const availabilityOptions = [
    { key: 'today', label: 'Today', icon: Zap },
    { key: 'tomorrow', label: 'Tomorrow', icon: Calendar },
    { key: 'thisWeek', label: 'This Week', icon: Calendar },
    { key: 'nextWeek', label: 'Next Week', icon: Calendar }
  ];

  const getAvailabilityColor = (status) => {
    switch (status) {
      case 'available': return 'from-green-500 to-emerald-600';
      case 'limited': return 'from-yellow-500 to-orange-500';
      case 'busy': return 'from-red-500 to-red-600';
      default: return 'from-gray-400 to-gray-500';
    }
  };

  const getAvailabilityBg = (status) => {
    switch (status) {
      case 'available': return 'from-green-50 to-emerald-50 border-green-200';
      case 'limited': return 'from-yellow-50 to-orange-50 border-orange-200';
      case 'busy': return 'from-red-50 to-red-50 border-red-200';
      default: return 'from-gray-50 to-gray-50 border-gray-200';
    }
  };

  const renderStars = (rating) => {
    return [...Array(5)].map((_, i) => (
      <Star
        key={i}
        className={`w-3 h-3 ${
          i < Math.floor(rating) 
            ? 'text-yellow-400 fill-yellow-400' 
            : 'text-gray-300'
        }`}
      />
    ));
  };

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <div className=" w-full mx-auto bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
      
      {/* Compact Header */}
      <div className="p-4">
        <div className="flex items-center space-x-3">
          {/* Worker Photo - Full Circle */}
          <div className="relative">
            <div className="w-14 h-14 rounded-full overflow-hidden shadow-lg border-3 border-white ring-2 ring-blue-100">
              <img 
                src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=387&q=80" 
                alt="Profile" 
                className="w-full h-full object-cover"
              />
            </div>
            {/* Online status indicator */}
            <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-gradient-to-r from-green-400 to-green-500 rounded-full border-2 border-white shadow-md">
              <div className="w-full h-full rounded-full bg-green-400 animate-ping opacity-75"></div>
            </div>
          </div>

          {/* Worker Info */}
          <div className="flex-1">
            <div className="flex items-center space-x-2 mb-1">
              <h3 className="text-lg font-bold text-gray-900">{workerData?.workerName}</h3>
              {workerData?.verified && (
                <div className="bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-full p-0.5">
                  <Award className="w-3 h-3" />
                </div>
              )}
            </div>
            <p className="text-sm text-gray-700 font-semibold">{workerData?.profession}</p>
            <p className="text-xs text-gray-500">{workerData?.category}</p>
          </div>

          {/* Rating & Expand Button */}
          <div className="text-right">
            <div className="flex items-center space-x-1 justify-end mb-1">
              {renderStars(workerData?.avgRating)}
            </div>
            <p className="text-sm font-semibold text-gray-900">{workerData?.avgRating}</p>
            <button
              onClick={toggleExpand}
              className="mt-1 bg-gray-100 hover:bg-gray-200 rounded-full p-1.5 transition-all duration-200 hover:scale-110 active:scale-95"
            >
              {isExpanded ? (
                <ChevronUp className="w-4 h-4 text-gray-600" />
              ) : (
                <ChevronDown className="w-4 h-4 text-gray-600" />
              )}
            </button>
          </div>
        </div>

        {/* Basic Info - Always Visible */}
        <div className="mt-3">
          {/* Skills Preview */}
          <div className="flex flex-wrap gap-2 mb-3">
            {workerData?.skills.slice(0, 3).map((skill, index) => (
              <span key={index} className="bg-blue-50 text-blue-700 px-2 py-1 rounded-full text-xs font-medium border border-blue-200">
                #{skill}
              </span>
            ))}
            {workerData?.skills.length > 3 && (
              <span className="bg-gray-50 text-gray-600 px-2 py-1 rounded-full text-xs font-medium border border-gray-200">
                +{workerData?.skills.length - 3}
              </span>
            )}
          </div>

          {/* Hourly Rate */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-2.5 border border-blue-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Clock className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-semibold text-gray-800">Hourly Rate</span>
              </div>
              <span className="text-sm font-bold text-blue-700">${workerData?.hourlyRate}/hour</span>
            </div>
          </div>
        </div>
      </div>

      {/* Expanded Content */}
      <div className={`transition-all duration-300 overflow-hidden ${
        isExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
      }`}>
        <div className="px-4 pb-4 space-y-3 border-t border-gray-100">
          
          {/* Description */}
          <div className="pt-3">
            <p className="text-gray-600 text-sm leading-relaxed">{workerData?.description}</p>
          </div>

          {/* Premium Availability Section */}
          <div className="bg-gradient-to-br from-slate-50 to-blue-50 rounded-xl p-3 border border-blue-100">
            <div className="flex items-center space-x-2 mb-2">
              <Calendar className="w-4 h-4 text-blue-600" />
              <h4 className="text-sm font-bold text-gray-800">Select Availability</h4>
            </div>
            
            <div className="grid grid-cols-2 gap-2">
              {/* {availabilityOptions?.map((option) => {
                const availability = workerData?.availability[option?.key];
                const Icon = option?.icon;
                const isSelected = selectedAvailability === option?.key;
                
                return (
                  <button
                    key={option?.key}
                    onClick={() => setSelectedAvailability(option?.key)}
                    className={`relative p-2 rounded-lg border transition-all duration-200 ${
                      isSelected 
                        ? `bg-gradient-to-br ${getAvailabilityBg(availability?.status)} border-blue-400 shadow-md` 
                        : `bg-white border-gray-200 hover:border-gray-300`
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-1.5">
                        <Icon className={`w-3 h-3 ${isSelected ? 'text-blue-600' : 'text-gray-500'}`} />
                        <span className="text-xs font-medium text-gray-700">{option?.label}</span>
                      </div>
                      
                      <div className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${
                        availability?.status === 'available' ? 'bg-green-100 text-green-700' :
                        availability?.status === 'limited' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        <div className={`w-1 h-1 rounded-full mr-1 bg-gradient-to-r ${getAvailabilityColor(availability?.status)}`}></div>
                        {availability?.status}
                      </div>
                    </div>
                  </button>
                );
              })} */}
              {availabilityOptions.map((option) => {
  const availability = availabilityData[option.key];
  const isSelected = selectedAvailability === option.key;
  const Icon = option.icon;

  return (
    <button key={option.key} onClick={() => setSelectedAvailability(option.key)} className="...">
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-1.5">
          <Icon className={`w-3 h-3 ${isSelected ? 'text-blue-600' : 'text-gray-500'}`} />
          <span className="text-xs font-medium">{option.label}</span>
        </div>
        <div className={`text-xs rounded-full px-1.5 py-0.5 font-medium ${
          availability?.status === 'available' ? 'bg-green-100 text-green-700' :
          availability?.status === 'limited' ? 'bg-yellow-100 text-yellow-700' :
          'bg-red-100 text-red-700'
        }`}>
          {availability?.status || "busy"}
        </div>
      </div>
    </button>
  );
})}

            </div>
          </div>

          {/* Additional Pricing */}
          <div className="bg-gray-50 rounded-lg p-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Visiting Fee</span>
              <span className="text-sm font-semibold text-gray-800">${workerData?.visitingFee}</span>
            </div>
            {workerData?.travelCharge && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Travel Charge</span>
                <span className="text-sm font-semibold text-gray-800">${workerData?.travelCharge}</span>
              </div>
            )}
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-2">
            <div className="text-center bg-blue-50 rounded-lg p-2 border border-blue-100">
              <p className="text-sm font-bold text-blue-700">{workerData?.completedJobs}</p>
              <p className="text-xs text-blue-600">Jobs</p>
            </div>
            <div className="text-center bg-green-50 rounded-lg p-2 border border-green-100">
              <p className="text-sm font-bold text-green-700">{workerData?.responseTime}</p>
              <p className="text-xs text-green-600">Response</p>
            </div>
            <div className="text-center bg-purple-50 rounded-lg p-2 border border-purple-100">
              <p className="text-sm font-bold text-purple-700">{workerData?.experience}</p>
              <p className="text-xs text-purple-600">Experience</p>
            </div>
          </div>

          {/* Location */}
          <div className="flex items-center space-x-2 bg-gray-50 rounded-lg p-2">
            <MapPin className="w-4 h-4 text-gray-600" />
            <span className="text-sm text-gray-600">{workerData?.baseLocation}</span>
          </div>
        </div>
      </div>

      {/* Action Buttons - Always Visible */}
      <div className="flex space-x-3 px-4 pb-4">
        <button onClick={()=> viewDeailsHandler(workerData?.workerId)} className="flex-1 bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-2.5 px-4 rounded-lg font-semibold hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 shadow-md hover:shadow-lg transform active:scale-95">
          View Details
        </button>
        <button className="flex-1 bg-white border-2 border-blue-600 text-blue-700 py-2.5 px-4 rounded-lg font-semibold hover:bg-blue-50 transition-all duration-200 shadow-sm hover:shadow-md transform active:scale-95">
          Contact
        </button>
      </div>
    </div>
  );
};

export default WorkerProfileCard;